import { createTheme } from '@mui/material/styles';

/**
 * Tokens de visualização (dataviz): série, grade, eixo e status. Ficam na paleta
 * para virarem variáveis CSS (`--mui-palette-viz-*`) que trocam com o tema —
 * o SVG dos gráficos usa as variáveis direto.
 */
declare module '@mui/material/styles' {
  interface Palette {
    viz: VizTokens;
  }
  interface PaletteOptions {
    viz?: VizTokens;
  }
}

interface VizTokens {
  series1: string;
  grid: string;
  axis: string;
  muted: string;
  good: string;
  warning: string;
  serious: string;
  critical: string;
  neutral: string;
}

// Paleta de status fixa (não muda com o tema) — distinta das cores de série.
const STATUS = { good: '#0ca30c', warning: '#fab219', serious: '#ec835a', critical: '#d03b3b' };

const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'data' },
  colorSchemes: {
    light: {
      palette: {
        primary: { main: '#2a78d6', contrastText: '#ffffff' },
        background: { default: '#f9f9f7', paper: '#fcfcfb' },
        text: { primary: '#0b0b0b', secondary: '#52514e', disabled: '#898781' },
        divider: 'rgba(11,11,11,0.10)',
        success: { main: '#006300' },
        error: { main: '#c0302f' },
        warning: { main: '#b26a00' },
        viz: {
          series1: '#2a78d6',
          grid: '#e1e0d9',
          axis: '#c3c2b7',
          muted: '#898781',
          neutral: '#a3a29b',
          ...STATUS,
        },
      },
    },
    dark: {
      palette: {
        primary: { main: '#3987e5', contrastText: '#ffffff' },
        background: { default: '#0d0d0d', paper: '#1a1a19' },
        text: { primary: '#ffffff', secondary: '#c3c2b7', disabled: '#898781' },
        divider: 'rgba(255,255,255,0.10)',
        success: { main: '#0ca30c' },
        error: { main: '#e66767' },
        warning: { main: '#fab219' },
        viz: {
          series1: '#3987e5',
          grid: '#2c2c2a',
          axis: '#383835',
          muted: '#898781',
          neutral: '#6f6e69',
          ...STATUS,
        },
      },
    },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: FONT,
    h1: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.01em' },
    h2: { fontSize: '1.125rem', fontWeight: 600 },
    h3: { fontSize: '0.95rem', fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
    overline: { fontSize: '0.7rem', letterSpacing: '0.06em', fontWeight: 600 },
  },
  components: {
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        // No escuro o MUI clareia superfícies elevadas (drawer, diálogo) com um
        // gradiente; o painel separa por borda e mantém a superfície neutra.
        root: { backgroundImage: 'none' },
        // Minimalista: separação por borda fina, não por sombra.
        outlined: ({ theme }) => ({ borderColor: theme.vars.palette.divider }),
      },
    },
    MuiButton: { defaultProps: { disableElevation: true } },
    MuiCard: { defaultProps: { variant: 'outlined' } },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({ borderColor: theme.vars.palette.divider }),
        head: ({ theme }) => ({
          color: theme.vars.palette.text.secondary,
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }),
      },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
    MuiTooltip: { defaultProps: { arrow: true } },
  },
});
